import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Calendar, Settings, AlertCircle } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useSubscription } from '../../contexts/SubscriptionContext';
import { subscriptionService } from '../../services/subscriptionService';
import MainLayout from '../../components/layout/MainLayout';
import Button from '../../components/ui/Button';
import SubscriptionCard from './components/SubscriptionCard';
import BillingHistory from './components/BillingHistory';
import PaymentMethods from './components/PaymentMethods';
import UsageMonitoring from './components/UsageMonitoring';
import PlanModification from './components/PlanModification';

const SubscriptionManagement = () => {
  const navigate = useNavigate();
  const { user, userProfile } = useAuth();
  const { subscription, hasActiveSubscription, refreshSubscription } = useSubscription();
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [portalLoading, setPortalLoading] = useState(false);

  useEffect(() => {
    if (user?.id) {
      loadData();
    }
  }, [user?.id]);

  const loadData = async () => {
    setLoading(true);
    try {
      const { data } = await subscriptionService?.getPaymentTransactions(user?.id);
      setTransactions(data || []);
    } catch (error) {
      console.error('Failed to load subscription data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleManageBilling = async () => {
    if (!subscription?.stripeCustomerId) {
      alert('No billing information found. Please upgrade to Pro first.');
      navigate('/pricing-plans');
      return;
    }

    setPortalLoading(true);
    try {
      const { data, error } = await subscriptionService?.createPortalSession(subscription?.stripeCustomerId);
      
      if (error) {
        alert(error?.message || 'Failed to open billing portal');
        return;
      }

      if (data?.url) {
        window.location.href = data?.url;
      }
    } catch (err) {
      alert('Failed to open billing portal. Please try again.');
    } finally {
      setPortalLoading(false);
    }
  };

  const handleUpgrade = () => {
    navigate('/pricing-plans');
  };

  if (loading) {
    return (
      <MainLayout>
        <div className="flex items-center justify-center min-h-screen">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <p className="text-gray-600">Loading subscription details...</p>
          </div>
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <div className="min-h-screen bg-gray-50 py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header */}
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-gray-900 mb-2">Subscription Management</h1>
            <p className="text-gray-600">Manage your subscription, billing, and payment preferences</p>
          </div>

          {/* Alert for Free Users */}
          {!hasActiveSubscription && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6 flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <h3 className="font-semibold text-blue-900 mb-1">Unlock Pro Features</h3>
                <p className="text-sm text-blue-800 mb-3">
                  Upgrade to Pro for 10x higher limits, reduced fees, and priority support.
                </p>
                <Button
                  onClick={handleUpgrade}
                  className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg text-sm font-medium"
                >
                  View Plans
                </Button>
              </div>
            </div>
          )}

          {/* Main Grid */}
          <div className="grid lg:grid-cols-3 gap-6 mb-6">
            {/* Subscription Card */}
            <div className="lg:col-span-2">
              <SubscriptionCard
                subscription={subscription}
                userProfile={userProfile}
                hasActiveSubscription={hasActiveSubscription}
                onManageBilling={handleManageBilling}
                portalLoading={portalLoading}
              />
            </div>

            {/* Quick Actions */}
            <div className="bg-white rounded-lg shadow-md p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <Settings className="w-5 h-5" />
                Quick Actions
              </h2>
              <div className="space-y-3">
                <Button
                  onClick={handleUpgrade}
                  className="w-full bg-blue-600 hover:bg-blue-700 text-white py-2 rounded-lg font-medium"
                >
                  {hasActiveSubscription ? 'View All Plans' : 'Upgrade to Pro'}
                </Button>
                {hasActiveSubscription && (
                  <Button
                    onClick={handleManageBilling}
                    disabled={portalLoading}
                    className="w-full bg-gray-100 hover:bg-gray-200 text-gray-900 py-2 rounded-lg font-medium"
                  >
                    {portalLoading ? 'Loading...' : 'Manage Billing'}
                  </Button>
                )}
                <Button
                  onClick={() => refreshSubscription()}
                  className="w-full bg-gray-100 hover:bg-gray-200 text-gray-900 py-2 rounded-lg font-medium"
                >
                  Refresh Status
                </Button>
              </div>

              {/* Billing Cycle Info */}
              {hasActiveSubscription && subscription?.currentPeriodEnd && (
                <div className="mt-6 pt-6 border-t border-gray-200">
                  <div className="flex items-center gap-2 text-sm text-gray-600 mb-2">
                    <Calendar className="w-4 h-4" />
                    <span>Next Billing Date</span>
                  </div>
                  <p className="text-lg font-semibold text-gray-900">
                    {new Date(subscription?.currentPeriodEnd)?.toLocaleDateString('en-US', {
                      month: 'long',
                      day: 'numeric',
                      year: 'numeric'
                    })}
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Usage Monitoring */}
          <div className="mb-6">
            <UsageMonitoring userProfile={userProfile} />
          </div>

          {/* Plan Modification */}
          {hasActiveSubscription && (
            <div className="mb-6">
              <PlanModification
                subscription={subscription}
                onUpgrade={handleUpgrade}
              />
            </div>
          )}

          {/* Billing History */}
          <div className="mb-6">
            <BillingHistory transactions={transactions} />
          </div>

          {/* Payment Methods */}
          {hasActiveSubscription && (
            <div>
              <PaymentMethods
                subscription={subscription}
                onManageBilling={handleManageBilling}
                portalLoading={portalLoading}
              />
            </div>
          )}
        </div>
      </div>
    </MainLayout>
  );
};

export default SubscriptionManagement;